// src/components/auth/ProtectedRoute.tsx

import React from 'react';
import { useAuth } from '../services/authService';
import { LoginForm } from './LoginForm';
import { COMPANY_COLORS } from '../constants/theme.constants';
import { Loader2 } from 'lucide-react';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { data: authState, isLoading, error } = useAuth();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center"
        style={{ 
          background: `linear-gradient(135deg, ${COMPANY_COLORS.pageBg}80, ${COMPANY_COLORS.surface})` 
        }}
      >
        <div className="text-center">
          <Loader2 
            size={48} 
            className="animate-spin mx-auto mb-4"
            style={{ color: COMPANY_COLORS.primary }}
          />
          <p className="text-gray-600 text-lg font-medium">Checking authentication...</p>
        </div>
      </div>
    );
  }

  // Show error if authentication check failed
  if (error) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center"
        style={{ 
          background: `linear-gradient(135deg, ${COMPANY_COLORS.pageBg}80, ${COMPANY_COLORS.surface})` 
        }}
      >
        <div className="text-center bg-white p-8 rounded-2xl shadow-lg">
          <div className="text-red-500 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Authentication Error</h2>
          <p className="text-gray-600 mb-4">Unable to verify your login status.</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg transition-colors duration-200"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Show login form if user is not authenticated
  if (!authState?.isAuthenticated) {
    return <LoginForm />;
  }

  // User is authenticated, render children
  return <>{children}</>;
};